/* ----------------------------------------------------------------------------
 * wakeMachine — the PURE, DOM-free decision core for hands-free "Hey Aura".
 * Mirrors the pattern of state/machine.ts + state/asyncGuard.ts: every hands-free
 * decision (when to score the wake model, when a score fires, when a captured
 * command has ended, whether THIS tab may listen, and whether to AUTO-arm on
 * return) lives here so it is unit-testable under Node --test with no browser,
 * no ONNX, no react. useHandsFree.ts is the thin glue that owns the real
 * getUserMedia / AudioContext / worklet / engine and calls into these.
 *
 * ZERO-PRE-WAKE-NETWORK is enforced structurally: no function here (nor the hook
 * seam that consumes them) performs any I/O; the ONLY thing that starts the STT
 * capture / greeting / assist is the controller call gated behind wakeFired().
 * -------------------------------------------------------------------------- */

/** Hands-free lifecycle phase (independent of the core VoiceStatus). */
export type HandsFreePhase =
  | 'disabled'   // kill switch off, or not opted-in on this device
  | 'blocked'    // another tab holds the single-tab lock
  | 'armed'      // locally listening for "Hey Aura" (wake scoring ACTIVE)
  | 'waking'     // wake fired; greeting playing; wake scoring PAUSED
  | 'capturing'  // command being recorded (controller Listening); VAD running
  | 'reprompting'// hands-free clarify: Aura SPEAKING the "which module?" question; scoring PAUSED
  | 'processing' // controller Thinking/Speaking; wake scoring PAUSED
  | 'muted';     // user muted; wake detection + TTS suspended

/** Wake scoring is ACTIVE only while armed and not muted. In every other phase
 *  (greeting, capturing, controller busy, muted, disabled, blocked) we DO NOT
 *  score, which prevents self-activation from the greeting/TTS and saves CPU. */
export function shouldScoreWake(phase: HandsFreePhase, muted: boolean): boolean {
  return phase === 'armed' && !muted;
}

/** A wake fires iff scoring is active AND the score meets the threshold. Passing
 *  the phase here makes it impossible to "fire" from a stale frame delivered
 *  while greeting/capturing (defensive double-gate over shouldScoreWake).
 *  `confirmed` (Phase C, marginal-fire fix, default true so every EXISTING
 *  caller/test is byte-unaffected): the glue's WakeConfirmGate sets this
 *  false for a MARGINAL score (>= threshold, < confidentCeiling) that has
 *  not yet held for enough consecutive frames -- see WakeConfirmGate below. */
export function wakeFired(
  phase: HandsFreePhase,
  muted: boolean,
  score: number | null,
  threshold: number,
  confirmed: boolean = true,
): boolean {
  if (!shouldScoreWake(phase, muted)) return false;
  if (!confirmed) return false;
  return typeof score === 'number' && Number.isFinite(score) && score >= threshold;
}

/* ---- VAD endpointer: decides when ONE hands-free command has ended --------- */

export interface EndpointerConfig {
  /** ABSOLUTE floor for the ADAPTIVE onset threshold (never below this). */
  onsetRms: number;
  /** ABSOLUTE floor for the ADAPTIVE silence threshold (never below this). */
  silenceRms: number;
  /** trailing silence (ms) after speech that ends the command. */
  silenceMs: number;
  /** hard cap (ms) on a single command regardless of silence. */
  maxMs: number;
  /** if no speech onset within this (ms), give up (cancel back to armed). */
  noSpeechMs: number;
  /** ADAPTIVE (owner #2.2, 2026-07-25): onset threshold = max(onsetRms, noiseFloor * onsetFactor). */
  onsetFactor: number;
  /** ADAPTIVE: voiced-hold/silence threshold = max(silenceRms, noiseFloor * silenceFactor). */
  silenceFactor: number;
  /** EMA smoothing for the ambient-noise floor estimate (per non-voiced frame). */
  noiseAlpha: number;
  /** consecutive frames at/above the onset threshold required to declare speech
   *  (noise-click guard; frames arrive every ~80ms at 16kHz/1280). */
  onsetConsecFrames: number;
}

/* Re-tuned for RAW capture (owner #2.2, 2026-07-25). The 2026-07-24 raw-capture
 * fix (echoCancellation/noiseSuppression/autoGainControl all FALSE) removed the
 * browser AGC these numbers were implicitly tuned against. Un-AGC'd Firefox
 * input runs quiet: the old ABSOLUTE onsetRms=0.02 missed speech onset entirely
 * (~50% dead 6s no-speech windows in the owner's 2026-07-25 diag session) and
 * silenceRms=0.015 classed quiet trailing words as silence (ffmpeg-proven
 * mid-utterance endpoint on "What's happening in [sales]"). The thresholds are
 * now ADAPTIVE to a per-capture ambient noise-floor EMA with LOW absolute
 * floors, and the tail is 1300ms so a natural mid-sentence pause ("update me
 * on … SEO") survives. Evidence: docs/verify/deploy2-1/device-retest-triage-
 * 2026-07-25.md; replay harness: __tests__/endpointer-replay.test.ts. */
/* CLASS-4 DSP FIX (voice-turn-audio-redesign Phase A, 2026-07-27): the retest3
 * SEO. blob (audio-gate GATE-1 seo_stall) proved these absolute floors still
 * too insensitive for a genuinely quiet, brief utterance on un-AGC input --
 * peak RMS 0.0069 crossed the old onsetRms=0.006 for a SINGLE 80ms frame
 * (frame 15) before falling to 0.0044 (frame 16), and onsetConsecFrames=2
 * requires two consecutive qualifying frames, so onset never latched
 * (verdicted no-speech at the 6s ceiling instead of ending near the real
 * word end, ~1.4-1.6s). Lowered onsetRms 0.006->0.0042 (just under the
 * observed frame-16 value, so frames 15+16 both qualify and latch on the
 * second of the two) and silenceRms 0.004->0.0028 in proportion (kept
 * onset > silence for the same debounce hysteresis). onsetConsecFrames is
 * UNCHANGED at 2 -- the click-immunity guard this class exists for is not
 * touched, only the absolute floor a quiet room is judged against. Replayed
 * against all three retest3 fixtures (seo_stall/no_stall/long_keywords in
 * audio-gate-endpointer-replay-2026-07-27.test.ts): seo_stall now endpoints
 * about 1.4s after the word (was: no-speech at 6s); no_stall and
 * long_keywords are UNCHANGED (their onset frames sit far enough above
 * 0.0042 that the lower floor never engages early). Legacy-sensitivity
 * guard preserved: this is STRICTLY more sensitive than shipped (lower
 * floor => easier to latch), never less; LEGACY_ONSET_CEIL /
 * LEGACY_SILENCE_CEIL (upper bounds) are untouched. */
export const DEFAULT_ENDPOINTER: EndpointerConfig = {
  onsetRms: 0.0042,
  silenceRms: 0.0028,
  silenceMs: 1300,
  maxMs: 12000,
  noSpeechMs: 6000,
  onsetFactor: 3.0,
  silenceFactor: 1.8,
  noiseAlpha: 0.1,
  onsetConsecFrames: 2,
};

export type EndpointResult = 'continue' | 'endpoint' | 'no-speech';

/** RMS of a frame in [-1,1]. Pure. */
export function frameRms(frame: Float32Array | number[]): number {
  let sum = 0;
  const n = frame.length;
  if (n === 0) return 0;
  for (let i = 0; i < n; i++) { const s = frame[i]; sum += s * s; }
  return Math.sqrt(sum / n);
}

/** Stateful (but pure-logic, no I/O) end-of-command detector. Fed one frame RMS
 *  + a monotonic clock per call; returns whether the command should end. */
export class CommandEndpointer {
  private readonly cfg: EndpointerConfig;
  private startedMs = -1;
  private lastVoiceMs = -1;
  private sawSpeech = false;
  private noiseRms = -1;      // ambient-floor EMA; seeded by the first frame
  private onsetRun = 0;       // consecutive frames at/above the onset threshold

  constructor(cfg: EndpointerConfig = DEFAULT_ENDPOINTER) {
    this.cfg = cfg;
  }

  /** LEGACY ceilings (IVG F1/F2, 2026-07-25): the adaptive thresholds may make
   *  the endpointer MORE sensitive than the shipped absolutes (0.02/0.015) but
   *  NEVER less. Caps the talk-over floor-seeding regression (loud speech seeds
   *  a high floor -> onset drifts unreachable) and bounds the quiet-speech EMA
   *  ratchet to at-worst-legacy behaviour. */
  private static readonly LEGACY_ONSET_CEIL = 0.02;
  private static readonly LEGACY_SILENCE_CEIL = 0.015;

  /** ADAPTIVE onset threshold (absolute-floored, legacy-ceilinged). */
  private onsetThresh(): number {
    if (this.noiseRms < 0) return this.cfg.onsetRms;
    const adaptive = Math.max(this.cfg.onsetRms, this.noiseRms * this.cfg.onsetFactor);
    return Math.min(adaptive, CommandEndpointer.LEGACY_ONSET_CEIL);
  }

  /** ADAPTIVE voiced-hold/silence threshold (absolute-floored, legacy-ceilinged, < onset). */
  private silenceThresh(): number {
    if (this.noiseRms < 0) return this.cfg.silenceRms;
    const adaptive = Math.max(this.cfg.silenceRms, this.noiseRms * this.cfg.silenceFactor);
    return Math.min(adaptive, CommandEndpointer.LEGACY_SILENCE_CEIL);
  }

  /** Call ONCE when capture begins, with the current clock. */
  start(nowMs: number): void {
    this.startedMs = nowMs;
    this.lastVoiceMs = nowMs;
    this.sawSpeech = false;
    this.noiseRms = -1;
    this.onsetRun = 0;
  }

  /** Feed a frame RMS at time nowMs. Returns the capture decision. */
  push(rms: number, nowMs: number): EndpointResult {
    if (this.startedMs < 0) this.start(nowMs);
    // Ambient-floor EMA: learn from every frame BELOW the current onset
    // threshold (i.e. plausibly not speech). Seeded by the first frame so a
    // quiet room calibrates low and a noisy room calibrates high.
    if (this.noiseRms < 0) {
      this.noiseRms = rms;
    } else if (rms < this.onsetThresh()) {
      this.noiseRms = this.noiseRms + this.cfg.noiseAlpha * (rms - this.noiseRms);
    }
    // Onset: require onsetConsecFrames consecutive frames at/above the adaptive
    // threshold (a lone click/thump never opens the voiced state).
    if (rms >= this.onsetThresh()) {
      this.onsetRun += 1;
      if (this.onsetRun >= this.cfg.onsetConsecFrames) this.sawSpeech = true;
    } else {
      this.onsetRun = 0;
    }
    // Voiced-hold: while speaking, ANY frame at/above the (lower) silence
    // threshold keeps the utterance alive — quiet word tails stay "voice".
    if (this.sawSpeech && rms >= this.silenceThresh()) {
      this.lastVoiceMs = nowMs;
    }
    // Hard cap always wins.
    if (nowMs - this.startedMs >= this.cfg.maxMs) return 'endpoint';
    if (!this.sawSpeech) {
      // Waiting for the user to start speaking.
      return nowMs - this.startedMs >= this.cfg.noSpeechMs ? 'no-speech' : 'continue';
    }
    // Speech has been seen: end on a sufficient trailing silence.
    if (rms < this.silenceThresh() && nowMs - this.lastVoiceMs >= this.cfg.silenceMs) return 'endpoint';
    return 'continue';
  }

  reset(): void {
    this.startedMs = -1;
    this.lastVoiceMs = -1;
    this.sawSpeech = false;
    this.noiseRms = -1;
    this.onsetRun = 0;
  }
}

/* ---- single-tab ownership -------------------------------------------------- */

export interface TabLock {
  ownerId: string;
  ts: number;
}

/** Decide whether THIS tab may claim the single-tab wake lock. A lock is stale
 *  (claimable) if absent, owned by us, or older than ttlMs (dead tab). Pure. */
export function canClaimTabLock(existing: TabLock | null, myId: string, now: number, ttlMs = 4000): boolean {
  if (!existing) return true;
  if (existing.ownerId === myId) return true;
  return now - existing.ts >= ttlMs;
}

/* ---- auto-arm on return ---------------------------------------------------- */

/** On page load / return: AUTO-arm ONLY when the device previously opted in AND
 *  the browser currently reports microphone permission as 'granted'. Any other
 *  permission state ('prompt' / 'denied' / unknown) stays MANUAL (a click). Pure. */
export function shouldAutoArm(optedIn: boolean, micPermission: 'granted' | 'denied' | 'prompt' | 'unknown'): boolean {
  return optedIn && micPermission === 'granted';
}

/* ---- pure hands-free FLOW reducer -----------------------------------------
 * The phase transitions + side-EFFECT decisions for the whole wake flow, as a
 * pure function so the entire "armed -> wake -> greeting -> capture -> process ->
 * re-arm" sequence (and the zero-pre-wake-network guarantee) is unit-testable
 * under Node --test. useHandsFree is the thin glue that maps each WakeEffect to a
 * real browser/controller call. NO effect that touches the network/mic pipeline
 * (beginWakeTurn / endWakeCommand) is EVER emitted before a SCORE event fires a
 * wake while armed — that is the structural zero-pre-wake-network proof.
 * -------------------------------------------------------------------------- */
import type { VoiceStatus } from '../types.ts';

export type WakeEvent =
  | { type: 'SCORE'; score: number | null; threshold: number; confirmed?: boolean }
  | { type: 'VAD'; decision: EndpointResult }
  | { type: 'GREETED' }
  | { type: 'CLARIFY_NEEDED' }   // hands-free: wake reply asked "which module?" — HOLD turn open (no re-arm)
  | { type: 'CLARIFY_SPOKEN' }   // hands-free: the "which module?" question TTS ended — capture answer IN-TURN
  | { type: 'WAKE_FAILED' }
  | { type: 'STATUS'; status: VoiceStatus }
  | { type: 'MUTE' }
  | { type: 'UNMUTE' }
  | { type: 'ARMED' }
  | { type: 'BLOCKED' }
  | { type: 'PTT_START' } // manual press-to-talk began: PAUSE wake (never tear it down)
  | { type: 'DISABLE' };

/** Side effects the glue must perform (in order). Only beginWakeTurn/
 *  endWakeCommand reach the real pipeline — and ONLY after a wake. */
export type WakeEffect =
  | 'beginWakeTurn'
  | 'endWakeCommand'
  | 'stop'
  | 'resetEngine'
  | 'resetEndpointer'
  | 'startEndpointer'
  | 'retryStatusAfterAnswering';

export interface WakeFlowResult {
  phase: HandsFreePhase;
  effects: WakeEffect[];
}

/** Pure phase+effect transition for the hands-free flow. `muted` is the live
 *  mute flag (kept outside the phase so Mute/Unmute compose cleanly). */
export interface WakeFlowContext {
  /** TRUE while Aura's own TTS audio is physically in flight OR within the
   *  post-playback grace window (turnEntity.isAnsweringOrGrace) -- the ONE
   *  true signal the STATUS case gates re-arm on (Phase C self-trigger fix,
   *  scope doc §3). Optional + defaults to false so every EXISTING call site
   *  that does not pass ctx is byte-unaffected. */
  answering?: boolean;
  /** EMPTY-CAPTURE REFRACTORY (voice residual #2, idle-falsefire-investigation-
   *  2026-07-28): TRUE while the glue's EmptyCaptureRefractory (below) is
   *  engaged -- N consecutive wake-triggered captures endpointed with NO usable
   *  STT result. A qualifying SCORE is IGNORED (logged by the glue, never acted
   *  on) instead of opening another doomed capture. Optional + defaults to
   *  false so every EXISTING call site is byte-unaffected. Orthogonal to score
   *  calibration (WAKE_THRESHOLD/confidentCeiling untouched) and to the
   *  answering context field above -- a behaviour-based backstop, not a
   *  score-based one, per the
   *  investigation's ranked recommendation (POST_CLOSE_COOLDOWN_MS precedent). */
  emptyCaptureRefractoryActive?: boolean;
  /** RAW-AUDIO INSTRUMENTATION (voice residual #2): TRUE (glue-computed: diag
   *  mode ON AND this is the PRIMARY wake capture, never the clarify-answer
   *  leg) reroutes a VAD 'no-speech' timeout through 'endWakeCommand' (the
   *  real STT upload path) instead of the silent local 'stop' discard, so the
   *  EXISTING server-side voiceDiagStt() blob-persist captures the raw audio
   *  for ground-truth analysis. OFF (default, diag off): byte-identical to
   *  pre-existing behaviour -- local discard, no network. */
  diagRawAudioCapture?: boolean;
}

export function stepWakeFlow(
  phase: HandsFreePhase,
  muted: boolean,
  ev: WakeEvent,
  ctx: WakeFlowContext = {},
): WakeFlowResult {
  switch (ev.type) {
    case 'ARMED':
      return { phase: muted ? 'muted' : 'armed', effects: ['resetEngine'] };
    case 'BLOCKED':
      return { phase: 'blocked', effects: [] };
    case 'DISABLE':
      return { phase: 'disabled', effects: ['stop'] };
    case 'MUTE':
      return { phase: phase === 'armed' ? 'muted' : phase, effects: [] };
    case 'UNMUTE':
      return phase === 'muted'
        ? { phase: 'armed', effects: ['resetEngine', 'resetEndpointer'] }
        : { phase, effects: [] };
    case 'PTT_START':
      // PTT-COEXIST GUARD (owner #2.2 P3, 2026-07-25): a manual Hold-to-talk press
      // must never orphan or tear down the wake flow. From any ACTIVE phase the
      // machine parks in 'processing' (scoring paused, endpointer reset) for the
      // duration of the PTT turn; the existing STATUS idle/error handler re-arms
      // when the turn settles. From 'capturing' the abandoned wake capture is
      // DISCARDED (no endWakeCommand — the PTT turn owns the mic now), which
      // also kills the old race where the wake VAD endpointed the user's PTT
      // speech mid-press. muted/disabled/blocked are untouched.
      if (phase === 'armed' || phase === 'waking' || phase === 'capturing' || phase === 'reprompting')
        return { phase: 'processing', effects: ['resetEndpointer'] };
      return { phase, effects: [] };
    case 'SCORE':
      if (!shouldScoreWake(phase, muted)) return { phase, effects: [] };
      // EMPTY-CAPTURE REFRACTORY (voice residual #2): ignore a qualifying wake
      // fire while the glue's EmptyCaptureRefractory is engaged -- no phase
      // change, no effects, so beginWakeTurn is NEVER emitted and no capture
      // opens. The glue beacons the suppression (never silent); this reducer
      // stays pure. Checked BEFORE wakeFired/WakeConfirmGate so it is a strict
      // superset gate, independent of score/confirmation logic.
      if (ctx.emptyCaptureRefractoryActive) return { phase, effects: [] };
      if (wakeFired(phase, muted, ev.score, ev.threshold, ev.confirmed ?? true)) return { phase: 'waking', effects: ['resetEndpointer', 'beginWakeTurn'] };
      return { phase, effects: [] };
    case 'GREETED':
      return phase === 'waking' ? { phase: 'capturing', effects: ['startEndpointer'] } : { phase, effects: [] };
    case 'CLARIFY_NEEDED':
      // The just-finished wake command was a briefing request needing a module choice. HOLD the
      // turn open in reprompting (Aura speaks the question). We do NOT pass through armed, so
      // shouldScoreWake stays false: NO wake re-scoring, no self-trigger, no re-wake to answer.
      return phase === 'processing' ? { phase: 'reprompting', effects: [] } : { phase, effects: [] };
    case 'CLARIFY_SPOKEN':
      // Question TTS finished — capture the spoken answer in the SAME turn, reusing the exact
      // command-capture path. reprompting -> capturing DIRECTLY (never via armed).
      return phase === 'reprompting' ? { phase: 'capturing', effects: ['startEndpointer'] } : { phase, effects: [] };
    case 'WAKE_FAILED':
      return { phase: muted ? 'muted' : 'armed', effects: ['resetEngine'] };
    case 'VAD':
      if (phase !== 'capturing') return { phase, effects: [] };
      if (ev.decision === 'endpoint') return { phase: 'processing', effects: ['endWakeCommand'] };
      if (ev.decision === 'no-speech') {
        // RAW-AUDIO INSTRUMENTATION (voice residual #2): diag ON (glue-scoped to
        // the PRIMARY capture only) reroutes the no-speech timeout through the
        // REAL endWakeCommand/STT path instead of the silent local discard, so
        // the existing voiceDiagStt() server persistence captures the raw audio.
        // The resulting (near-certainly empty) STT outcome is recorded by the
        // glue exactly like any other empty capture -- no double-counting (the
        // glue does not also count this event locally when diag is on). Default
        // (diag off): byte-identical to pre-existing behaviour.
        if (ctx.diagRawAudioCapture) return { phase: 'processing', effects: ['endWakeCommand'] };
        return { phase: 'processing', effects: ['stop', 'resetEndpointer'] };
      }
      return { phase, effects: [] };
    case 'STATUS': {
      const toIdleOrError = ev.status === 'idle' || ev.status === 'error';
      const toBusy = ev.status === 'speaking' || ev.status === 'thinking';
      if (toIdleOrError && (phase === 'processing' || phase === 'capturing' || phase === 'waking')) {
        // SELF-TRIGGER FIX (Phase C, voice-turn-audio-redesign, 2026-07-27): a
        // coarse STATUS idle/error must NEVER re-arm scoring while the turn
        // entity reports Aura's own audio genuinely still in flight (or in its
        // post-playback grace window) -- closes the wake_score_fire 0.8042
        // mid-answer class (retest3 evidence, scope doc §2/§3). Scoped to
        // 'processing' only: that is the post-answer phase; 'capturing'/
        // 'waking' idle/error paths are capture-abort cases with no TTS
        // involved and are UNCHANGED. HOLD (do not re-arm) and let the glue
        // reschedule the same check (retryStatusAfterAnswering) instead of
        // silently dropping the event.
        if (phase === 'processing' && ctx.answering) return { phase, effects: ['retryStatusAfterAnswering'] };
        return { phase: muted ? 'muted' : 'armed', effects: ['resetEngine', 'resetEndpointer'] };
      }
      // Belt-and-suspenders (Phase C): whenever STATUS reports Aura's own
      // pipeline thinking/speaking, FORCE scoring OUT of 'armed' too -- before
      // this fix only 'capturing' was covered, so a phase that reached
      // 'armed' via any earlier race stayed armed through the WHOLE spoken
      // answer with no second checkpoint. Harmless for the PTT path (PTT_START
      // already parks phase out of 'armed' before its own turn ever reaches
      // speaking/thinking).
      if (toBusy && (phase === 'capturing' || phase === 'armed')) return { phase: 'processing', effects: [] };
      return { phase, effects: [] };
    }
    default: {
      const _never: never = ev;
      return _never;
    }
  }
}

/* ---- SCOPED-BRIEFING CLARIFY: pure decision core (hands-free only) ---------
 * These pure, DOM-free, network-free helpers back the hands-free "which module?"
 * clarify flow. They live here (the wake decision core) so they are unit-testable
 * under Node --test alongside the flow reducer. The GLUE (useVoiceController /
 * useHandsFree) performs the two network calls (Endpoint 1 module menu, Endpoint 2
 * scoped assist) and the TTS; ALL branching logic is decided by these functions.
 *
 * §D DYNAMIC MODULE SET (binding): the answer matcher derives its vocabulary FROM
 * the live Endpoint-1 `modules` array (keys + labels) — never a compiled-in key
 * list. An UNKNOWN-BUT-ENTITLED key (present in Endpoint 1 but absent from the
 * optional synonym overlay) is STILL selectable by its key/label. Synonyms for the
 * currently-known keys are an OPTIONAL overlay only.
 * -------------------------------------------------------------------------- */

/** One selectable briefing module as returned by Endpoint 1 (GET /v1/ai/voice/briefing-modules). */
export interface BriefingModule {
  key: string;
  label: string;
}

/** OPTIONAL synonym overlay for the currently-known module keys. NOT the source
 *  of truth (the live menu is) — a convenience so natural phrasings map to a key.
 *  BUDGET is deliberately ABSENT (contract AMENDMENT v3: budget is excluded from
 *  the clarify menu, so it must never be matchable via a synonym here). */
export const KNOWN_MODULE_SYNONYMS: Readonly<Record<string, readonly string[]>> = {
  sales: ['sales', 'lead', 'leads', 'pipeline', 'deal', 'deals'],
  seo: ['seo', 'search', 'ranking', 'rankings', 'keyword', 'keywords', 'organic'],
  social: ['social', 'post', 'posts', 'social media'],
  campaigns: ['campaign', 'campaigns', 'outreach', 'sending', 'email', 'emails'],
};

/** Words that mean "the whole briefing" (== the normal full-briefing path). */
const ALL_TOKENS = ['all', 'everything', 'the lot', 'both', 'all of them', 'whole', 'full'];

/** Lowercase + collapse punctuation to spaces (padded) for robust word matching. */
function normalizeAnswer(t: string): string {
  return ' ' + t.toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim() + ' ';
}

/* ---- SHARED MODULE-ASK + BRIEFING-INTENT CLASSIFIER (FE <-> BE PARITY) -----
 * The FE and BE "briefing intent" + "module-scoped ask" classifiers MUST agree
 * byte-for-byte (this mismatch shipped once because they were verified apart).
 * Everything below is the FE MIRROR of AiRoutes.php (isBriefingIntent /
 * isBriefingUtterance / moduleScopedAsk). Both directions are pinned by the ONE
 * shared oracle fixture
 * bookedcentral-api/ai-service/tests/fixtures/briefing-intent-parity.json (read by
 * BOTH the FE vitest AND the BE php test) and the module-ask table mirror
 * bookedcentral-api/ai-service/tests/fixtures/module-ask-table.json.
 *
 * OVERVIEW vs DETAIL (owner directive 2026-07-25): a module-ask fires ONLY on an
 * OVERVIEW leading phrase + a WHOLE-REMAINDER that EQUALS a MODULE NAME. Detail
 * nouns (ranking/rankings, opportunities, keywords, traffic, positions, fix) are
 * NOT modules, so "what's happening with ranking" / "what are my SEO
 * opportunities" stay RICH (conversational), while "tell me about SEO" /
 * "what's happening in SEO" become the SHORT scoped briefing.
 * -------------------------------------------------------------------------- */

/** OVERVIEW leading phrases (apostrophe-stripped). MIRROR of BE
 *  AiRoutes::MODULE_ASK_PATTERNS — asserted byte-identical by the parity test. */
export const MODULE_ASK_PATTERNS: readonly string[] = [
  'tell me about my ', 'tell me about ',
  'hows my ', 'how is my ', 'what about my ',
  'whats happening in ', 'whats happening with ',
  'what is happening in ', 'what is happening with ',
  'whats going on in ', 'whats going on with ',
  'what is going on in ', 'what is going on with ',
  'update me on ', 'update me about ',
  'whats new in ', 'what is new in ',
  'status update on ', 'give me a status update on ',
  'give me an update on ', 'update on ',
];

/** MODULE-LEVEL names ONLY (no detail nouns). key = the canonical briefing
 *  module_scope key the BE menu validates (crm|seo|social|marketing). MIRROR of
 *  BE AiRoutes::MODULE_ASK_TERMS — asserted byte-identical by the parity test. */
export const MODULE_ASK_TERMS: Readonly<Record<string, readonly string[]>> = {
  crm: ['sales', 'crm', 'lead', 'leads'],
  seo: ['seo', 'search'],
  social: ['social', 'social media'],
  marketing: ['campaigns', 'campaign', 'marketing'],
};

/** Optional leading POSSESSIVE stripped from the module-ask REMAINDER so
 *  "my <module>" / "our <module>" == the bare module for EVERY overview phrase
 *  (owner directive 2026-07-25 C2). MIRROR of BE AiRoutes::POSSESSIVE_PREFIXES. */
export const POSSESSIVE_PREFIXES: readonly string[] = ['my ', 'our '];

/** Optional leading ARTICLE stripped from the module-ask REMAINDER AFTER the
 *  possessive strip (owner directive 2026-07-25 #2 robustify) so "the <module>"
 *  / "a <module>" == the bare module for every overview phrase. Never stripped
 *  to empty. MIRROR of BE AiRoutes::ARTICLE_PREFIXES. */
export const ARTICLE_PREFIXES: readonly string[] = ['the ', 'a ', 'an '];

/** ONE optional TRAILING FILLER stripped from the module-ask REMAINDER (owner
 *  directive 2026-07-25 #2 robustify) to absorb STT tails ("seo please" / "SEO
 *  today"). A BOUNDED list of NON-detail words ONLY -- NEVER a detail noun
 *  (ranking/rankings/opportunities/keywords/traffic/positions/fix stay RICH).
 *  Stripped only when the remainder has >1 token so a lone filler never scopes.
 *  MIRROR of BE AiRoutes::TRAILING_FILLERS. */
export const TRAILING_FILLERS: readonly string[] = ['please', 'thanks', 'now', 'stuff', 'thing', 'today'];

/* ---- PART #5 keyword-anchored module routing + WIDEN A/B shared lists --------
 * Deterministic, NOT phrase-list-prefix-dependent: ANY overview-shaped phrasing
 * whose CLOSED-VOCABULARY remainder is EXACTLY one module term routes to that
 * module (owner directive 2026-07-25 #5). All lists are byte-pinned by the parity
 * fixture tests/fixtures/intent-widening-table.json and mirrored on the BE
 * (AiRoutes.php). FP-safety is non-negotiable: any leftover token (detail noun,
 * foreign entity, second module) falls through UNCHANGED to the rich path. ------ */

/** Prepositions stripped (ONE) from the keyword-anchor REMAINDER. MIRROR of BE
 *  AiRoutes::PREPOSITIONS. */
export const PREPOSITIONS: readonly string[] = ['in', 'with', 'on', 'for', 'about', 'of'];

/** WIDEN option-B trailing time/scope qualifiers (the BASE four today|now|right
 *  now|this morning stay inline in isBriefingUtterance). Shared by that matcher's
 *  qualifier set AND the keyword-anchor trailing strip. MIRROR of BE
 *  AiRoutes::TRAILING_QUALIFIERS. */
export const TRAILING_QUALIFIERS: readonly string[] = ['this week', 'lately', 'so far', 'at the moment', 'currently', 'this month', 'overall', 'in general'];

/** Leading SINGLE-word conversational fillers (never BEGIN a stem, so stripping
 *  can only EXPOSE a stem). Shared by isBriefingUtterance + keyword-anchor. MIRROR
 *  of BE AiRoutes::LEAD_FILLERS. */
export const LEAD_FILLERS: readonly string[] = ['hey', 'hi', 'hello', 'so', 'ok', 'okay', 'well', 'please', 'um', 'uh', 'aura', 'lets'];

/** Leading AFFIRMATIVE/filler tokens stripped from the WHOLE module-ask core
 *  BEFORE the MODULE_ASK_PATTERNS prefix loop AND the #5 keyword-anchored
 *  fallback (voice-fix-wave-3, 2026-07-26). A follow-up-turn STT artifact often
 *  PREPENDS an affirmative ("Yes. Tell me about the SEO today") which broke every
 *  prefix match -> the ask fell through to the rich domain-detail path. Bounded
 *  set, stripped left-to-right, NEVER to empty (a leading-filler-only utterance
 *  keeps its last token and cannot become a false module scope). BYTE-MIRRORED
 *  with BE AiRoutes::MODULE_ASK_LEAD_STRIP (same tokens, same order). */
export const MODULE_ASK_LEAD_STRIP: readonly string[] = ['yes', 'yeah', 'yep', 'ok', 'okay', 'so', 'um', 'uh', 'sure', 'well', 'hi', 'hey', 'hello', 'lets', 'please'];

/** Strip leading MODULE_ASK_LEAD_STRIP tokens from a normalised core; never to
 *  empty. MIRROR of BE AiRoutes::stripModuleAskLead. */
function stripModuleAskLead(core: string): string {
  const toks = core.split(' ');
  while (toks.length > 1 && MODULE_ASK_LEAD_STRIP.includes(toks[0])) toks.shift();
  return toks.join(' ');
}

/** Leading MULTI-word conversational fillers (no trailing space). Stripped as an
 *  ALTERNATIVE candidate (never destructively) so a stem that BEGINS with one
 *  ("tell me what i need to know") still matches. MIRROR of BE MULTI_FILLERS. */
export const MULTI_FILLERS: readonly string[] = ['tell me', 'give me', 'can you', 'could you', 'i want to know', 'i wanna know'];

/** Whole-utterance BRIEFING question stems (apostrophe-stripped). The SINGLE
 *  source used by BOTH isBriefingUtterance (equality) AND the keyword-anchor
 *  router (OVERVIEW stems). MIRROR of BE AiRoutes::BRIEFING_UTTERANCE_STEMS. */
export const BRIEFING_UTTERANCE_STEMS: readonly string[] = [
  'whats up', 'what is up',
  'whats happening', 'what is happening',
  'whats going on', 'what is going on',
  'hows everything', 'how is everything',
  'how are things',
  'update me', 'update me on whats happening', 'update me on what is happening',
  'what do i need to know',
  'whats the update', 'what is the update',
  'whats new', 'what is new',
  'tell me what i need to know',
  'give me an update',
  'anything i need to know',
  'anything new',
  'status update', 'a status update',
];

/** OVERVIEW stems for keyword-anchored routing: MODULE_ASK_PATTERNS (trailing
 *  preposition trimmed) UNION BRIEFING_UTTERANCE_STEMS, de-duplicated + sorted
 *  longest-first so a more specific stem wins. MIRROR of BE keywordAnchorStems(). */
const KEYWORD_ANCHOR_STEMS: readonly string[] = [
  ...new Set<string>([...MODULE_ASK_PATTERNS.map((p) => p.trim()), ...BRIEFING_UTTERANCE_STEMS]),
].sort((a, b) => b.length - a.length);

/** ONE trailing filler/qualifier removed from the keyword-anchor remainder:
 *  TRAILING_FILLERS UNION TRAILING_QUALIFIERS. Never strips to empty (a lone
 *  qualifier never scopes). MIRROR of BE stripOneTrailing source. */
const KEYWORD_TRAILING: readonly string[] = [...TRAILING_FILLERS, ...TRAILING_QUALIFIERS];

/** Strip ONE trailing filler/qualifier (longest-first; multi-word aware); never
 *  to empty. MIRROR of BE AiRoutes::stripOneTrailing. */
function stripOneTrailing(rem: string): string {
  const sorted = [...KEYWORD_TRAILING].sort((a, b) => b.length - a.length);
  for (const q of sorted) {
    if (rem === q) return rem;
    if (rem.endsWith(' ' + q)) return rem.slice(0, rem.length - (q.length + 1)).trim();
  }
  return rem;
}

/** Decompose ONE candidate core into a single module key, else null. Removes a
 *  leading overview stem, then ONE preposition, ONE possessive, ONE article, ONE
 *  trailing filler/qualifier; the remainder must EQUAL exactly one module term.
 *  MIRROR of BE AiRoutes::anchorDecompose. */
function anchorDecompose(text: string): string[] | null {
  for (const stem of KEYWORD_ANCHOR_STEMS) {
    if (text !== stem && !text.startsWith(stem + ' ')) continue;
    let rem = text === stem ? '' : text.slice(stem.length + 1).trim();
    // ONE leading preposition (absorbs a connector a tier-2 stem lacks).
    const pp = rem === '' ? [] : rem.split(' ');
    if (pp.length > 1 && PREPOSITIONS.includes(pp[0])) { pp.shift(); rem = pp.join(' '); }
    // ONE leading possessive, never to empty.
    for (const poss of POSSESSIVE_PREFIXES) {
      if (rem.startsWith(poss)) { rem = rem.slice(poss.length).trim(); break; }
    }
    // ONE leading article, never to empty.
    for (const art of ARTICLE_PREFIXES) {
      if (rem.startsWith(art) && rem.length > art.length) { rem = rem.slice(art.length).trim(); break; }
    }
    // ONE trailing filler/qualifier, never to empty.
    rem = stripOneTrailing(rem);
    if (rem !== '') {
      for (const key of Object.keys(MODULE_ASK_TERMS)) {
        if (MODULE_ASK_TERMS[key].includes(rem)) return [key];
      }
    }
  }
  return null;
}

/** Keyword-anchored module routing (PART #5). ANY overview-shaped phrasing whose
 *  closed-vocabulary remainder is EXACTLY one module term -> that module's SHORT
 *  scoped summary; any leftover token -> null (rich path). Strips leading single-
 *  word fillers, then tries the base + each ALTERNATIVE multi-word-filler-stripped
 *  candidate. MIRROR of BE AiRoutes::keywordAnchoredScope. */
function keywordAnchoredScope(core: string): string[] | null {
  if (core === '') return null;
  const toks = core.split(' ');
  while (toks.length > 0 && LEAD_FILLERS.includes(toks[0])) toks.shift();
  const base = toks.join(' ');
  if (base === '') return null;
  const candidates = [base];
  for (const mf of MULTI_FILLERS) {
    if (base.startsWith(mf + ' ')) {
      const rest = base.slice(mf.length + 1).trim();
      if (rest !== '') candidates.push(rest);
    }
  }
  for (const cand of candidates) {
    const hit = anchorDecompose(cand);
    if (hit) return hit;
  }
  return null;
}

/** Normalise for the module-ask + whole-utterance match: drop apostrophes so
 *  "how's" -> "hows", lowercase, collapse non-alphanumerics to single spaces,
 *  trim. MIRROR of BE AiRoutes::moduleAskNormalise / isBriefingUtterance core. */
function briefNormalise(t: string): string {
  return t.replace(/['\u2019\u2018`]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Module-scoped OVERVIEW ask -> canonical module key(s), else null. EQUALITY on
 *  the whole remainder (one module token, nothing more). Detail/multi-word
 *  remainders ("with ranking", "my stripe account", "seo opportunities") -> null
 *  (stay RICH). MIRROR of BE AiRoutes::moduleScopedAsk. Hands-free ONLY. */
export function detectModuleAsk(transcript: string): string[] | null {
  // Strip a leading affirmative/filler run BEFORE both the prefix loop and the
  // #5 keyword-anchored fallback (voice-fix-wave-3): "yes tell me about the seo"
  // must scope like "tell me about the seo". Mirrors BE moduleScopedAsk.
  const core = stripModuleAskLead(briefNormalise(transcript));
  if (core === '') return null;
  for (const pat of MODULE_ASK_PATTERNS) {
    if (core.startsWith(pat)) {
      let remainder = core.slice(pat.length).trim();
      // Possessive parity (owner 2026-07-25 C2): treat "my <module>" / "our
      // <module>" IDENTICALLY to the bare module across EVERY overview phrase.
      // Strip ONE optional leading possessive before the whole-remainder
      // equality; never to empty (bare "my"/"our" must not scope), so a
      // non-module remainder ("my rankings" -> "rankings") stays RICH.
      for (const poss of POSSESSIVE_PREFIXES) {
        if (remainder.startsWith(poss)) { remainder = remainder.slice(poss.length).trim(); break; }
      }
      // Leading ARTICLE strip (owner 2026-07-25 #2): "the <module>" / "a
      // <module>" == the bare module. AFTER the possessive; never to empty.
      for (const art of ARTICLE_PREFIXES) {
        if (remainder.startsWith(art) && remainder.length > art.length) { remainder = remainder.slice(art.length).trim(); break; }
      }
      // ONE trailing FILLER strip (owner 2026-07-25 #2): absorb an STT tail
      // ("seo please" / "seo today") WITHOUT touching detail nouns. Only when
      // >1 token so a lone filler never becomes a module.
      const remParts = remainder.split(' ');
      if (remParts.length > 1 && TRAILING_FILLERS.includes(remParts[remParts.length - 1])) {
        remParts.pop();
        remainder = remParts.join(' ').trim();
      }
      if (remainder !== '') {
        for (const key of Object.keys(MODULE_ASK_TERMS)) {
          if (MODULE_ASK_TERMS[key].includes(remainder)) return [key];
        }
      }
    }
  }
  // PART #5 fallback: keyword-anchored routing for overview-shaped phrasings that
  // are not a bare MODULE_ASK_PATTERN prefix ("tell me what's happening in sales
  // today"). FP-safe closed-vocabulary remainder check; null keeps the rich path.
  return keywordAnchoredScope(core);
}

/** Unambiguous BRIEFING phrases matched WHOLE-TOKEN (substring) anywhere. MIRROR
 *  of BE RecommendationIntent::BRIEFING. The ambiguous conversational forms
 *  ("whats happening", "whats going on", "hows everything", "how are things")
 *  are DELIBERATELY excluded — they live WHOLE-utterance in isBriefingUtterance
 *  so both apostrophe forms classify identically and "…with ranking" stays RICH
 *  (apostrophe-inconsistency #3 fix, owner 2026-07-25). */
const BRIEFING_PHRASES: readonly string[] = [
  'briefing', 'brief me', 'give me a briefing', 'daily briefing',
  'morning briefing', 'summary', 'summarise', 'summarize',
  'summarise everything', 'summarize everything', 'overview', 'recap',
  'overall status', 'overall', 'state of the business',
  'state of play', 'how are we doing', 'catch me up', 'fill me in',
  'bring me up to speed', 'where do things stand',
  'the big picture', 'my dashboard', 'everything', 'full picture',
];

/** BRIEFING openers/extras the BE isBriefingIntent local-supplements. MIRROR of
 *  the BE parity supplement (rundown / daily update / ...). */
const BRIEFING_EXTRA: readonly string[] = ['rundown', 'run down', 'daily update', 'morning update'];

/** Whole-token normalise (apostrophe -> space, space-padded). MIRROR of BE norm /
 *  RecommendationIntent::normalise. */
function briefNormSpace(prompt: string): string {
  return ' ' + prompt.toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim() + ' ';
}

/** Whole-utterance conversational briefing matcher — MIRROR of BE
 *  AiRoutes::isBriefingUtterance. TRUE only when the WHOLE normalised utterance IS
 *  a briefing question-phrase (after stripping bounded leading fillers), optionally
 *  + ONE trailing generic qualifier. NOT substring, so "what's happening with
 *  ranking" -> false. */
function isBriefingUtterance(prompt: string): boolean {
  let core = briefNormalise(prompt);
  if (core === '') return false;
  const fillers = LEAD_FILLERS;
  const tokens = core.split(' ');
  while (tokens.length > 0 && fillers.includes(tokens[0])) tokens.shift();
  core = tokens.join(' ');
  if (core === '') return false;
  const phrases = BRIEFING_UTTERANCE_STEMS;
  const multiFillers = MULTI_FILLERS.map((f) => f + ' ');
  const candidates = [core];
  for (const mf of multiFillers) {
    if (core.startsWith(mf)) {
      const rest = core.slice(mf.length).trim();
      if (rest !== '') candidates.push(rest);
    }
  }
  const qualifiers = ['today', 'now', 'right now', 'this morning', ...TRAILING_QUALIFIERS];
  for (const cand of candidates) {
    if (phrases.includes(cand)) return true;
    for (const q of qualifiers) {
      const suffix = ' ' + q;
      if (cand.endsWith(suffix)) {
        const head = cand.slice(0, -suffix.length).trim();
        if (head !== '' && phrases.includes(head)) return true;
      }
    }
  }
  return false;
}

/** Briefing-intent detector — MIRROR of BE AiRoutes::isBriefingIntent. Hands-free
 *  ONLY (PTT never calls this). A GENERAL briefing ask -> the full-briefing clarify
 *  flow; a MODULE-scoped ask is detected separately by detectModuleAsk(). Deliberately
 *  NOT substring on the ambiguous conversational forms, so "what's happening with
 *  ranking" (a detail ask) never triggers the clarify menu. */
export function isBriefingIntent(transcript: string): boolean {
  const norm = briefNormSpace(transcript);
  const trimmed = norm.trim();
  if (trimmed === 'opening' || trimmed === 'opening today' || trimmed === 'open today') return true;
  for (const op of [' opening today ', ' opening the day ', ' opening my day ', ' open the day ', ' start my day ', ' start the day ']) {
    if (norm.includes(op)) return true;
  }
  for (const ph of [' todays briefing ', ' today s briefing ']) {
    if (norm.includes(ph)) return true;
  }
  for (const ph of BRIEFING_EXTRA) {
    if (norm.includes(' ' + ph + ' ')) return true;
  }
  for (const ph of BRIEFING_PHRASES) {
    if (norm.includes(' ' + ph + ' ')) return true;
  }
  return isBriefingUtterance(transcript);
}

/** The clarify branch decision from the live module menu (Endpoint 1). */
export type ClarifyDecision =
  | { kind: 'full' }                       // 0 modules -> skip clarify, full briefing
  | { kind: 'auto'; scope: string[] }      // exactly 1 -> skip the question, auto-scope to it
  | { kind: 'ask'; modules: BriefingModule[] }; // >1 -> ask "which module?"

/** 0 -> full briefing; 1 -> auto-scope (no question); >1 -> ask. Non-array / empty
 *  is treated as 0 (honest fall-through to the full briefing). */
export function decideClarify(modules: BriefingModule[] | null | undefined): ClarifyDecision {
  const list = Array.isArray(modules) ? modules.filter((m) => m && typeof m.key === 'string' && m.key !== '') : [];
  if (list.length === 0) return { kind: 'full' };
  if (list.length === 1) return { kind: 'auto', scope: [list[0].key] };
  return { kind: 'ask', modules: list };
}

/** The parsed spoken answer to the "which module?" question. */
export type AnswerMatch =
  | { kind: 'all' }                       // "all"/"everything"/... -> the full briefing
  | { kind: 'scope'; keys: string[] }     // one or more offered modules
  | { kind: 'unrecognised' };             // nothing offered matched (and not "all")

/**
 * Match a spoken answer against the OFFERED modules (closed set). Vocabulary is
 * derived FROM the offered `modules` (each key + its label) — §D DYNAMIC: an
 * unknown-but-entitled key is selectable by key/label even with no synonym entry.
 * `synonyms` (default KNOWN_MODULE_SYNONYMS) is an OPTIONAL overlay for known keys.
 * Multi-answer ("leads and campaigns") yields every matched key. "all" wins only
 * when NO specific module matched. Unrelated speech matches nothing -> unrecognised.
 */
export function matchModuleAnswer(
  transcript: string,
  modules: BriefingModule[],
  synonyms: Readonly<Record<string, readonly string[]>> = KNOWN_MODULE_SYNONYMS,
): AnswerMatch {
  const t = normalizeAnswer(transcript);
  if (!t.trim()) return { kind: 'unrecognised' };

  const offered = Array.isArray(modules) ? modules.filter((m) => m && typeof m.key === 'string' && m.key !== '') : [];
  const matched: string[] = [];
  for (const m of offered) {
    // Vocabulary FROM the live menu: the canonical key and the display label.
    const terms = new Set<string>();
    terms.add(m.key.toLowerCase());
    if (typeof m.label === 'string' && m.label.trim()) terms.add(m.label.toLowerCase());
    // OPTIONAL overlay: synonyms indexed by the normalized display LABEL (robust
    // to key drift — the live menu keys, e.g. crm/marketing, differ from the label)
    // with a key fallback. Never the source of truth; key+label above always match.
    const labelKey = typeof m.label === 'string' ? m.label.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() : '';
    for (const sk of [labelKey, m.key.toLowerCase()]) {
      const syn = sk ? synonyms[sk] : undefined;
      if (syn) for (const w of syn) terms.add(w.toLowerCase());
    }
    for (const term of terms) {
      const norm = term.replace(/[^a-z0-9]+/g, ' ').trim();
      if (norm && t.includes(' ' + norm + ' ')) { matched.push(m.key); break; }
    }
  }
  if (matched.length > 0) return { kind: 'scope', keys: matched };

  if (ALL_TOKENS.some((w) => t.includes(' ' + w + ' '))) return { kind: 'all' };

  return { kind: 'unrecognised' };
}

/** The next step after parsing a clarify answer, given how many re-asks already
 *  happened. Unrecognised -> re-ask ONCE, then fall back to the full briefing;
 *  never a dead end. "all" == the full briefing. */
export type ClarifyStep =
  | { kind: 'scope'; keys: string[] }  // fetch Endpoint 2 scoped to these keys
  | { kind: 'all' }                    // full briefing (== "all")
  | { kind: 're-ask' }                 // ask the question once more, capture again
  | { kind: 'full' };                  // give up matching -> full briefing fallback

/** maximum number of re-asks before falling back to the full briefing. */
export const CLARIFY_MAX_REASKS = 1;

export function resolveClarifyAnswer(match: AnswerMatch, reAskCount: number): ClarifyStep {
  if (match.kind === 'all') return { kind: 'all' };
  if (match.kind === 'scope') return { kind: 'scope', keys: match.keys };
  return reAskCount < CLARIFY_MAX_REASKS ? { kind: 're-ask' } : { kind: 'full' };
}


/* ---- TURN-CLOSE (owner #2.2 P1, 2026-07-25): the follow-up loop EXIT --------
 * "Is there anything else?" -> "no" MUST close the turn and stop listening.
 * Live defect 19:28:08Z: "Nope. Shut it." routed conversational -> the LLM
 * answered -> re-armed -> loop (docs/verify/deploy2-1/device-retest-triage-
 * 2026-07-25.md #3). A BOUNDED, WHOLE-utterance negative-intent set, matched
 * AFTER the same normalisation + leading-filler strip as isBriefingUtterance,
 * so "Okay, no, that's all" closes but "no, tell me about sales" does NOT
 * (whole-utterance equality — any extra content falls through to routing).
 * MIRROR of BE AiRoutes::isTurnClose + TURN_CLOSE_PHRASES (parity fixture
 * ai-service/tests/fixtures/turn-close-parity.json). */
export const TURN_CLOSE_PHRASES: readonly string[] = [
  'no', 'nope', 'nah', 'nothing', 'nothing else', 'no thanks', 'no thank you',
  'nothing thanks', 'thats all', 'that is all', 'thats it', 'that is it',
  'thats all thanks', 'no thats all', 'no thats it', 'im done', 'i am done',
  'done', 'all done', 'im good', 'i am good', 'all good', 'were done',
  'we are done', 'stop', 'stop listening', 'shut it', 'shut it down',
  'thats everything', 'that is everything', 'no nothing', 'no nothing else',
  'goodbye', 'bye', 'bye bye', 'thank you', 'thanks',
  'no more', 'thats enough', 'that is enough', 'im finished', 'i am finished',
  'be quiet', 'go to sleep', 'thatll be all', 'that will be all', 'thank you so much',
  'shut up',
];

/** Leading conversational fillers stripped before the whole-utterance match
 *  (SAME set as isBriefingUtterance — keep in sync). Exported so the parity
 *  suite byte-pins it to the shared mirror (IVG F6). */
export const TURN_CLOSE_LEAD_FILLERS = ['hey', 'hi', 'hello', 'so', 'ok', 'okay', 'well', 'please', 'um', 'uh', 'aura', 'lets', 'oh'];

/** TRUE iff the WHOLE normalised utterance (after leading-filler strip) is a
 *  turn-close phrase OR a CONCATENATION of close phrases ("Nope. Shut it." ->
 *  "nope"+"shut it" — the live 19:28:08Z defect utterance). Any non-close
 *  content anywhere makes the segmentation fail, so "no, tell me about sales"
 *  NEVER closes. Pure. MIRROR of BE AiRoutes::isTurnClose. */
export function isTurnClose(transcript: string): boolean {
  const norm = transcript.replace(/['\u2019\u2018`]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (norm === '') return false;
  const tokens = norm.split(' ');
  while (tokens.length > 1 && TURN_CLOSE_LEAD_FILLERS.includes(tokens[0])) tokens.shift();
  // Full segmentation (DP): every token must belong to some close phrase.
  const maxLen = Math.max(...TURN_CLOSE_PHRASES.map((ph) => ph.split(' ').length));
  const n = tokens.length;
  const dp: boolean[] = new Array(n + 1).fill(false);
  dp[0] = true;
  for (let i = 1; i <= n; i++) {
    for (let l = 1; l <= maxLen && l <= i; l++) {
      if (dp[i - l] && TURN_CLOSE_PHRASES.includes(tokens.slice(i - l, i).join(' '))) { dp[i] = true; break; }
    }
  }
  return dp[n];
}

/** The short spoken acknowledgement for a closed turn (FE + BE aligned). */
export const TURN_CLOSE_ACK = 'Okay.';


/* ---- WAKE-SCORE MONITOR (owner deploy-GO directive, 2026-07-25) ------------
 * Missed/near-miss "Hey Aura" wakes are INVISIBLE in diag: a wake that never
 * fires emits no event, so dead attempts can't be distinguished from silence.
 * This pure, DOM-free monitor turns the per-frame classifier scores into a
 * bounded stream of diagnostic events (the glue beacons them ONLY when the
 * voice-diag flag is on):
 *   fire      — score >= threshold (a wake fired)
 *   near_miss — a score EPISODE peaked inside [nearFloor, threshold): the user
 *               probably said the wake words but the classifier fell short.
 *               One event per episode (hysteresis), never per frame.
 *   summary   — every summaryMs while scoring: frames/peak/nearMisses/fires,
 *               so "how often did it nearly-miss" is MEASURED, not inferred.
 * Pure logic here so it is unit-testable; NO I/O, NO beaconing in this class. */
export interface WakeScoreEvent {
  type: 'fire' | 'near_miss' | 'summary';
  /** fire: the firing score · near_miss: the episode peak · summary: window peak. */
  score: number;
  /** near_miss only: episode length in ms. */
  durationMs?: number;
  /** summary only: window stats. */
  frames?: number;
  nearMisses?: number;
  fires?: number;
}

export class WakeScoreMonitor {
  private readonly threshold: number;
  private readonly nearFloor: number;
  private readonly summaryMs: number;
  private frames = 0;
  private peak = 0;
  private windowNear = 0;
  private windowFires = 0;
  private episodeActive = false;
  private episodePeak = 0;
  private episodeStartMs = 0;
  private lastSummaryMs = -1;

  constructor(threshold: number, nearFloorFactor = 0.6, summaryMs = 30000) {
    this.threshold = threshold;
    this.nearFloor = threshold * nearFloorFactor;
    this.summaryMs = summaryMs;
  }

  /** Feed one frame score (null = engine skipped the frame). Returns the
   *  diagnostic events this frame produced (usually none). */
  push(score: number | null, nowMs: number): WakeScoreEvent[] {
    const out: WakeScoreEvent[] = [];
    if (this.lastSummaryMs < 0) this.lastSummaryMs = nowMs;
    if (typeof score === 'number' && Number.isFinite(score)) {
      this.frames += 1;
      if (score > this.peak) this.peak = score;
      if (score >= this.threshold) {
        // A fire absorbs any open episode (it stopped being a near-miss).
        this.episodeActive = false;
        this.windowFires += 1;
        out.push({ type: 'fire', score });
      } else if (score >= this.nearFloor) {
        if (!this.episodeActive) {
          this.episodeActive = true;
          this.episodeStartMs = nowMs;
          this.episodePeak = score;
        } else if (score > this.episodePeak) {
          this.episodePeak = score;
        }
      } else if (this.episodeActive) {
        // Episode closed below the floor: ONE near-miss event with the peak.
        this.episodeActive = false;
        this.windowNear += 1;
        out.push({ type: 'near_miss', score: this.episodePeak, durationMs: Math.max(0, nowMs - this.episodeStartMs) });
      }
    }
    if (nowMs - this.lastSummaryMs >= this.summaryMs && this.frames > 0) {
      out.push({ type: 'summary', score: this.peak, frames: this.frames, nearMisses: this.windowNear, fires: this.windowFires });
      this.frames = 0; this.peak = 0; this.windowNear = 0; this.windowFires = 0;
      this.lastSummaryMs = nowMs;
    }
    return out;
  }
}

/* ---- MARGINAL-FIRE CONFIRMATION GATE (Phase C, voice-turn-audio-redesign,
 * 2026-07-27) -----------------------------------------------------------
 * Owner-observed defect: a LONE ~80ms frame scoring just above WAKE_THRESHOLD
 * (the 0.42-0.52 class) fired a real wake and opened a full 6s command
 * capture that found no speech -- retest3 14:27-14:29 UTC produced a doom
 * loop of these empty captures. `wakeFired` today has zero confirmation: a
 * single qualifying frame is enough. This mirrors CommandEndpointer's
 * onsetConsecFrames click-immunity idea, applied to the wake scorer instead
 * of the endpointer: a MARGINAL score (>= threshold but < confidentCeiling)
 * must hold for `consecFrames` consecutive qualifying frames before it is
 * allowed to fire; a CONFIDENT score (>= confidentCeiling) fires on the very
 * FIRST qualifying frame -- exactly today's behaviour, unchanged. Every real
 * fire in the preserved diag evidence (including the self-trigger's own
 * 0.8042) sits far above confidentCeiling, so genuine "Hey Aura" detections
 * are not delayed by this change. HONEST GAP: the real positive-control
 * capture (owner capture-exercise, still pending per the redesign scope doc)
 * is the outstanding LIVE proof for the wake phrase itself against this
 * gate -- this class + its unit tests prove the LOGIC only; documented, not
 * assumed. Pure, stateful (no I/O) -- one instance per armed session,
 * instantiated in the glue exactly like WakeScoreMonitor. */
export interface WakeConfirmConfig {
  /** consecutive qualifying (>= threshold) frames required before a MARGINAL
   *  score is allowed to fire. */
  consecFrames: number;
  /** scores at/above this ceiling fire immediately (first qualifying frame),
   *  same as pre-fix behaviour -- only the band [threshold, confidentCeiling)
   *  needs confirmation. */
  confidentCeiling: number;
}

export const DEFAULT_WAKE_CONFIRM: WakeConfirmConfig = {
  consecFrames: 2,
  confidentCeiling: 0.60,
};

export class WakeConfirmGate {
  private readonly cfg: WakeConfirmConfig;
  private streak = 0;

  constructor(cfg: WakeConfirmConfig = DEFAULT_WAKE_CONFIRM) {
    this.cfg = cfg;
  }

  /** Feed ONE frame's score (call every armed-scoring frame, not just
   *  qualifying ones, so the streak resets correctly on a dip). Returns TRUE
   *  iff THIS frame is allowed to fire (wakeFired's `confirmed` argument). */
  admit(score: number | null, threshold: number): boolean {
    if (typeof score !== 'number' || !Number.isFinite(score) || score < threshold) {
      this.streak = 0;
      return false;
    }
    if (score >= this.cfg.confidentCeiling) {
      this.streak = 0;
      return true;
    }
    this.streak += 1;
    if (this.streak >= this.cfg.consecFrames) {
      this.streak = 0;
      return true;
    }
    return false;
  }

  reset(): void {
    this.streak = 0;
  }
}

/* ---- EMPTY-CAPTURE REFRACTORY/BACKOFF (voice residual #2, idle-falsefire-
 * investigation-2026-07-28) -----------------------------------------------
 * The investigation's ranked recommendation #2 (2026-07-28): the idle
 * 0.79-0.99 false-fire cluster is INDETERMINATE on score evidence alone (the
 * one 'confident genuine wake' data point, 0.8042, was reclassified as a
 * TTS self-trigger -- no safe threshold/confidentCeiling re-anchor exists).
 * The one BUILD-BLIND, GATE-PROVABLE fix is behaviour-based, not score-based:
 * every documented fire in the cluster opened a capture that endpointed
 * EMPTY (zero STT, zero preserved audio) -- so debounce on EMPTINESS itself,
 * exactly the POST_CLOSE_COOLDOWN_MS precedent (handsFreeConfig.ts), applied
 * to a different trigger condition. A wake fire is NEVER individually wrong
 * or right here -- this only stops a STREAK of empty captures from stacking
 * into the loop the owner reproduced.
 *
 * Pure, stateful, no I/O -- one instance per armed session, instantiated in
 * the glue exactly like WakeConfirmGate/WakeScoreMonitor. Does NOT touch
 * WAKE_THRESHOLD, confidentCeiling, POST_CLOSE_COOLDOWN_MS, the answering-
 * hold, or PTT (PTT never reaches the SCORE/VAD cases this gates).
 * -------------------------------------------------------------------------- */
export interface EmptyCaptureRefractoryConfig {
  /** consecutive empty captures required to ENGAGE the refractory. 2-3 per
   *  the investigation's own recommendation (never 1 -- a single empty
   *  capture is routine, e.g. a genuine-but-too-quiet attempt, and must get
   *  an immediate retry, not a backoff). */
  tripCount: number;
  /** refractory window (ms) applied the FIRST time it engages. 6000 == one
   *  CaptureWindow noSpeechMs cycle (DEFAULT_ENDPOINTER.noSpeechMs) -- long
   *  enough that a genuinely stray cluster cannot immediately re-open another
   *  doomed capture, short enough that a real "Hey Aura" moments later is
   *  only briefly delayed, never silently dropped forever. */
  baseWindowMs: number;
  /** each RE-engagement (another tripCount run of empties immediately after
   *  a prior window elapses, with no successful capture in between) scales
   *  the window by this factor -- an escalating backoff for a genuinely
   *  persistent false-fire source, capped by maxWindowMs. */
  escalationFactor: number;
  /** hard ceiling on the refractory window regardless of escalation, so a
   *  pathological run can never suppress wake for an unbounded time. */
  maxWindowMs: number;
}

export const DEFAULT_EMPTY_CAPTURE_REFRACTORY: EmptyCaptureRefractoryConfig = {
  tripCount: 3,
  baseWindowMs: 6000,
  escalationFactor: 2,
  maxWindowMs: 30000,
};

export class EmptyCaptureRefractory {
  private readonly cfg: EmptyCaptureRefractoryConfig;
  private streak = 0;
  private engagedLevel = 0;
  private untilMs = 0;

  constructor(cfg: EmptyCaptureRefractoryConfig = DEFAULT_EMPTY_CAPTURE_REFRACTORY) {
    this.cfg = cfg;
  }

  /** how many times the refractory has engaged since the last successful
   *  (real-content) capture -- 0 before the first engagement this streak. */
  get level(): number {
    return this.engagedLevel;
  }

  /** Record ONE wake-triggered capture that endpointed with NO usable result
   *  (no STT / empty transcript / stt_failed -- see useVoiceController's
   *  WakeTurnOutcome 'empty'). Returns TRUE iff THIS call just ENGAGED the
   *  refractory (streak reached tripCount) -- the glue beacons on that edge. */
  recordEmpty(nowMs: number): boolean {
    this.streak += 1;
    if (this.streak >= this.cfg.tripCount) {
      this.streak = 0;
      this.engagedLevel += 1;
      const window = Math.min(
        this.cfg.baseWindowMs * Math.pow(this.cfg.escalationFactor, this.engagedLevel - 1),
        this.cfg.maxWindowMs,
      );
      this.untilMs = nowMs + window;
      return true;
    }
    return false;
  }

  /** A wake-triggered capture produced REAL content (a genuine turn, a
   *  clarify trigger -- anything but 'empty'). Fully resets: a legitimate
   *  wake is NEVER penalised by a prior unrelated empty streak. */
  recordSuccess(): void {
    this.streak = 0;
    this.engagedLevel = 0;
    this.untilMs = 0;
  }

  /** TRUE while a wake fire should be ignored (logged, not acted on). */
  isActive(nowMs: number): boolean {
    return nowMs < this.untilMs;
  }

  /** ms remaining in the active window (0 when not active) -- beacon detail. */
  remainingMs(nowMs: number): number {
    return Math.max(0, this.untilMs - nowMs);
  }

  reset(): void {
    this.streak = 0;
    this.engagedLevel = 0;
    this.untilMs = 0;
  }
}
